import { useLazyQuery } from "@apollo/client"
import { useCallback, useEffect, useRef, useState } from "react"
import { GET_SESSION_TIMEOUT_CONFIG } from "./queries"
import { LogError } from "../../helpers/logger"

const defaultLogoutAfter = 60 * 60 * 1000 //60 minutes of inactivity → offline/logout
const defaultWarningDuration = 30 * 1000 //30 seconds warning before logout
const defaultRefreshInterval = 3 * 60 * 60 * 1000 // 3 hours in milliseconds
const minimumRefreshInterval = 60 * 60 * 1000 // 1 hour in milliseconds


export default function useActivityConfig() {
  const refreshIntervalRef = useRef(defaultRefreshInterval)
  const logoutAfterRef = useRef(defaultLogoutAfter)
  const warningDurationRef = useRef(defaultWarningDuration)
  const [loadingConfig, setLoadingConfig] = useState(true)

  const [getConfig] = useLazyQuery(GET_SESSION_TIMEOUT_CONFIG, {
    fetchPolicy: "network-only",
  })

  const fetchConfig = useCallback(async () => {
    try{
      const { data, error: queryError } = await getConfig()

      if (queryError) {
        LogError("GraphQL error fetching activity config", queryError)
      }

      const configArray = data?.GetGlobalConfig || []

      //Find the allowed inactivity value in minutes and set it as the logoutAfter value (converted to ms)
      const logoutAfterConfigValue = configArray.find(
        (item) => item.key === "userInactivityLimitMinutes",
      )?.value

      const newLogoutAfterValueMs = logoutAfterConfigValue ? logoutAfterConfigValue * 60 * 1000 : logoutAfterRef.current

      if (newLogoutAfterValueMs && newLogoutAfterValueMs !== logoutAfterRef.current) {
        logoutAfterRef.current = newLogoutAfterValueMs
      }

      //Find the allowed inactivity response time in seconds and set it as the warningDuration value (converted to ms)    
      const warningDurationConfigValue = configArray.find(
        (item) => item.key === "userInactivityResponseSeconds",
      )?.value

      const newWarningDurationValueMs = warningDurationConfigValue ? warningDurationConfigValue * 1000 : warningDurationRef.current

      if (newWarningDurationValueMs && newWarningDurationValueMs !== warningDurationRef.current) {
        warningDurationRef.current = newWarningDurationValueMs
      }

      //find the config refresh interval in hours and set it as the refreshInterval value (converted to ms)
      const refreshIntervalConfigValue = configArray.find(
        (item) => item.key === "userConfigRefreshIntervalHours",
      )?.value

      if (refreshIntervalConfigValue) {
        const newInterval = refreshIntervalConfigValue * 60 * 60 * 1000
        
        if (!isNaN(newInterval)) {
          // Ensure a minimum refresh interval (e.g., 1 hour) to prevent excessive polling
          refreshIntervalRef.current = Math.max(newInterval, minimumRefreshInterval)
        }
      }
    }catch(error){
      LogError("Error fetching activity config values, using defaults", error)
    }    

    //Notify the system that the config has finished loading as best it could, allowing the activity tracker to start monitoring user activity 
    setLoadingConfig(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getConfig])

  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    // Set up a recursive timeout to refresh config every x hours
    const run = async () => {
      await fetchConfig();
      if (isMounted) {
        timeoutId = setTimeout(run, refreshIntervalRef.current);
      }
    };

    //Run the timeout
    run();
    
    // Cleanup timeout on unmount and set isMounted to false to prevent timeouts leaking through
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [fetchConfig]);


  return {
    getLogoutAfter: useCallback(() => logoutAfterRef.current, []),
    getWarningDuration: useCallback(() => warningDurationRef.current, []),
    loadingConfig,
  }
}